import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  type ButtonProps,
  Card,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "~/common/mui";

import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { useTranslation } from "react-i18next";
import type { LineItem, PlainLineItem } from "~/features/cart/cart/models/CartData";
import { ContractItemParam, QuoteItemParam } from "~/features/cart/cart/components/LineItemStaticParams";
import { Price as PriceV3 } from "~/domains/pricing/components/Price/v3/Price";
import { PriceElements } from "~/domains/pricing/components/Price/PriceV4";
import { Parameter } from "~/common/components/ParameterV2";
import DateInput from "~/common/components/DateInput";
import { getEDDLabel } from "~/features/cart/cart/utils/cartUtils";
import { DateFormatType, formatDate } from "~/common/utils/DateFormatter";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { ProductParams2 } from "~/domains/product/components/ProductParamsV2";
import { useLineItemActions, useLineItemData } from "~/domains/cart/hooks";
import { LineItemMessagesSection } from "~/domains/cart/components";
import { SaleDiscountIndicator } from "~/domains/salesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";

import { useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";

import { CommentInput, useShowAndScrollInput } from "~/common/components/CommentInput/CommentInput";
import { LineItemStockIndicator } from "../cart/components/LineItemStockIndicator";
import { PartialDeliveriesSection } from "./PartialDeliveriesSection";
import { EditablePackagingCell } from "../cart/components/PackagingCell";
import { SaleQuantityIndicator } from "~/domains/salesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { useSelector } from "react-redux";
import { selectSubLineItems } from "../cart/redux/selectors/cartSelectors";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import type { HousingProduct, KITProductData } from "~/domains/product/types/ProductData";
import { Product } from "~/domains/product/Product";
import { OptionalPartEntry } from "./OptionalPartEntry";
import Delete from "~/common/mui/icons/Delete";
import { ToggleCommentButton } from "./ToggleCommentButton";
import {
  IncludedSurchargeAsterisk,
  TariffSurchargeAsterisk,
  OptionalBox,
  CurrentPrice,
  OldPrice,
} from "./commonComponents";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { ProductLineGrid } from "./ProductLineGrid";
import * as PriceUtils from "~/utils/PriceUtils";

interface LineItemComponentProps {
  className?: string;
  lineItem: LineItem | PlainLineItem;
  product: ProductDescriptor | ProductDescriptor<KITProductData>;
  onDelete?: () => void;
}

const DeleteButton = (props: ButtonProps) => {
  return <Button variant="text" color="textPrimary" startIcon={<Delete />} {...props} />;
};

interface IncludedPartTileProps {
  housingProduct: HousingProduct;
  kitQuantity: number;
}

const IncludedPartTile = ({ housingProduct, kitQuantity }: IncludedPartTileProps) => {
  return (
    <Product.Context value={housingProduct.product}>
      <Card component={Stack} direction={"row"} gap={2} sx={{ p: 2, height: "100%" }}>
        <Product.PrimaryImageWithLogosV2 size={64} gap={8} />
        <Stack gap={1}>
          <Product.DesignationV2 fontWeight={"bold"} />
          <Parameter label="Quantity" value={housingProduct.quantity * kitQuantity} />
        </Stack>
      </Card>
    </Product.Context>
  );
};

export const CartProductLine = ({ className, lineItem, product, onDelete }: LineItemComponentProps) => {
  const { t } = useTranslation();

  const isKit = "includedParts" in product.base;

  const { pricing, sales, validation, flags, messages } = useLineItemData({ lineItem, product });
  const actions = useLineItemActions({ lineItem, product });

  const { isOnSale, saleActive, salesPricing, salesStock } = sales;
  const { quantityVisible, isPMA, inStock } = flags;
  const { disableEditing } = validation;

  const netPrice = pricing.netPrice;
  const tariffSurcharge = pricing.tariffSurcharge;
  const surchargeValue = pricing.surchargeValue;
  const price = saleActive && salesPricing ? salesPricing.price : netPrice;
  const oldPrice = saleActive && salesPricing ? salesPricing.oldPrice : undefined;
  const discountFormatted = saleActive && salesPricing ? salesPricing.discountFormatted : undefined;

  const totalPrice = pricing.totalPrice;

  const showPriceReferenceSlot = canSeePriceRefColumn() || isOnSale;

  const aliases = ProductParams2.ProductAliases({
    mode: "15-digits",
    visibleAliasesLayout: "separate-rows",
    packCode: lineItem.packCode,
  })({
    t,
    product,
  });

  const gpsd = ProductParams2.GPSD({
    t,
    product,
  });

  const isMinDesktopM = useIsMinDesktopSizeM();

  const [commentRef, toggleComment] = useShowAndScrollInput(actions.toggleShowComment);

  const optionalPartLineItems = useSelector(selectSubLineItems(lineItem.id));

  let kitAccordionSlot: React.ReactNode = undefined;

  if (isKit) {
    const kitProduct = product as ProductDescriptor<KITProductData>;
    const hasIncludedParts = kitProduct.base.includedParts.length > 0;
    const incluedPartsTotalQty = kitProduct.base.includedParts.reduce(
      (sum, part) => sum + part.quantity * lineItem.quantity,
      0,
    );

    const optionalPartsTotalQty = optionalPartLineItems.reduce((sum, item) => sum + item.quantity, 0);
    const optionalPartsTotalValue = PriceUtils.volatileSum(
      ...optionalPartLineItems.map((item) => getTotalPrice(item.netPrice, item.quantity)),
    );

    const selectedParts = kitProduct.base.optionalParts
      .map((part) => ({
        part,
        lineItem: optionalPartLineItems.find((item) => item.productCode === part.product.code),
      }))
      .filter((item) => item.lineItem) as {
      part: HousingProduct;
      lineItem: LineItem;
    }[];

    kitAccordionSlot = (
      <Card sx={{ bgcolor: "surface.main" }}>
        {hasIncludedParts && (
          <>
            <Accordion
              sx={(theme) => ({
                "&::before": { display: "none !important" },
                "&::after": { display: "none !important" },
                bgcolor: "common.white",
                transition: theme.transitions.create("background-color"),
                "&.Mui-expanded": {
                  bgcolor: "surface.main",
                },
              })}
            >
              <AccordionSummary>
                <Stack gap={1}>
                  {t("page.cart.housing.included.title")}
                  <Stack direction="row" gap={2} sx={{ fontWeight: "normal" }}>
                    <Parameter
                      variant="row"
                      label={t("cartPage.table.item", { count: incluedPartsTotalQty })}
                      value={incluedPartsTotalQty}
                    />
                  </Stack>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container columns={{ phone: 2, "desktop-m": 4 }} gap={2}>
                  {kitProduct.base.includedParts.map((part) => (
                    <Grid key={part.product.designation} size={1}>
                      <IncludedPartTile housingProduct={part} kitQuantity={lineItem.quantity} />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Divider flexItem />
          </>
        )}

        <Accordion
          sx={(theme) => ({
            "&::before": { display: "none !important" },
            "&::after": { display: "none !important" },
            bgcolor: "common.white",
            transition: theme.transitions.create("background-color"),
            "&.Mui-expanded": {
              bgcolor: "surface.main",
            },
          })}
        >
          <AccordionSummary>
            <Stack gap={1}>
              {t("page.cart.housing.optional.title")}
              <Stack direction="row" gap={2} sx={{ fontWeight: "normal" }}>
                <Parameter
                  variant="row"
                  label={t("cartPage.table.item", { count: optionalPartsTotalQty })}
                  value={optionalPartsTotalQty}
                />
                {optionalPartsTotalValue !== undefined && (
                  <Parameter
                    variant="row"
                    label={t("cartPage.table.header.total.title")}
                    value={<strong>{PriceUtils.text(optionalPartsTotalValue)}</strong>}
                  />
                )}
              </Stack>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack gap={2}>
              {selectedParts.map(({ part, lineItem: optionalItem }) => (
                <OptionalPartEntry key={part.designation} product={part} lineItem={optionalItem} editMode={true} />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3 }} component={Stack} gap={3} className={className}>
      <ProductLineGrid>
        <ProductLineGrid.StockIndicator>
          <LineItemStockIndicator product={product} packEntry={lineItem} />
        </ProductLineGrid.StockIndicator>

        <ProductLineGrid.Quantity>
          <Stack gap={1}>
            <QuantityInput
              variant="compact"
              value={lineItem.quantity}
              step={validation.minQuantityValue}
              onValueChange={actions.updateQty}
              error={validation.quantityHasError}
              helperLabel={validation.MOQHelperLabel}
              disabled={disableEditing}
              min={0}
              label={false}
            />
            {salesStock > 0 && saleActive && <SaleQuantityIndicator.V2 quantity={salesStock} />}
          </Stack>
        </ProductLineGrid.Quantity>

        {netPrice && (
          <ProductLineGrid.NetPrice>
            <Parameter
              label={<PriceElements.NetPriceLabel variant="inherit" price={netPrice} />}
              value={
                <PriceElements.Stack gap={0}>
                  <Stack direction="row" gap={1}>
                    <CurrentPrice
                      price={price}
                      zeroText={t("product.priceOnRequest")}
                      tariffSurcharge={tariffSurcharge}
                      variant="inherit"
                    />
                    {discountFormatted && (
                      <SaleDiscountIndicator.V2 variant="bodyMedium" discountFormatted={discountFormatted} />
                    )}
                  </Stack>
                  {oldPrice && <OldPrice price={oldPrice} variant="bodySmall" tariffSurcharge={tariffSurcharge} />}
                  {!saleActive && (
                    <Typography variant="bodySmall">
                      <PriceV3.ExtraLabels price={netPrice} surchargeEnabled={false} />
                    </Typography>
                  )}
                </PriceElements.Stack>
              }
            />
          </ProductLineGrid.NetPrice>
        )}

        {totalPrice && (
          <ProductLineGrid.TotalPrice>
            <Parameter
              label={<PriceElements.TotalPriceLabel variant="inherit" price={totalPrice} />}
              value={
                <CurrentPrice price={totalPrice} variant="inherit" tariffSurcharge={tariffSurcharge} surchargeEnabled />
              }
            />
          </ProductLineGrid.TotalPrice>
        )}

        <ProductLineGrid.DeliveryDate>
          {quantityVisible && !isPMA ? (
            <DateInput
              label={t("cartPage.table.header.delivery.title")}
              helperLabel={getEDDLabel(lineItem, t)}
              date={lineItem.manuallySetRDD ? lineItem.deliveryDate : null}
              placeholder={
                !lineItem.manuallySetRDD && lineItem.deliveryDate
                  ? formatDate(lineItem.deliveryDate, DateFormatType.DATE)
                  : undefined
              }
              onChange={actions.updateDate}
              onClear={actions.clearDate}
              showClear={lineItem.manuallySetRDD}
              isInvalid={!lineItem.deliveryDate}
              isDayDisabled={actions.isDayDisabled}
              disabled={disableEditing}
            />
          ) : (
            <div>-</div>
          )}
        </ProductLineGrid.DeliveryDate>

        <ProductLineGrid.Packaging>
          <EditablePackagingCell product={product} lineItem={lineItem} onChange={actions.selectPackage} />
        </ProductLineGrid.Packaging>

        {showPriceReferenceSlot && (
          <ProductLineGrid.PriceReference>
            <Stack>
              <PriceReferenceParam
                product={product.base}
                priceReference={lineItem.priceReference}
                onChange={actions.updatePriceReference}
                data-test-id="package-entry-price-reference"
                disabled={disableEditing || saleActive}
                editable={!lineItem.quoteNumber && canSeePriceRefColumn()}
                size={isMinDesktopM ? "small" : "medium"}
              />
              <CheckboxWithLabel
                checked={saleActive}
                onCheck={actions.updateSalesEnabled}
                hidden={!isOnSale || !!lineItem.priceReference}
              >
                {t("feature.salesCampaign.specialDealPrice")}
              </CheckboxWithLabel>
            </Stack>
          </ProductLineGrid.PriceReference>
        )}

        {aliases && (
          <ProductLineGrid.Aliases>
            <Parameter {...aliases} />
          </ProductLineGrid.Aliases>
        )}

        {gpsd && (
          <ProductLineGrid.GPSD>
            <Parameter {...gpsd} />
          </ProductLineGrid.GPSD>
        )}

        <ProductLineGrid.QuoteDetails>
          <QuoteItemParam product={product.base} lineItem={lineItem} />
        </ProductLineGrid.QuoteDetails>

        <ProductLineGrid.ContractDetails>
          <ContractItemParam product={product.base} lineItem={lineItem} />
        </ProductLineGrid.ContractDetails>

        {isLineLevelCustomerOrderNumberEnabled() && (
          <ProductLineGrid.PONumber>
            <TextField
              fullWidth
              label={t("search.type.customerOrderNumber")}
              inputProps={{ "data-test-id": "package-entry-po-number" }}
              value={lineItem.customerOrderNumber}
              onChange={actions.updatePONumber}
            />
          </ProductLineGrid.PONumber>
        )}

        <ProductLineGrid.Messages>
          <LineItemMessagesSection
            messages={messages}
            isPMA={isPMA}
            isOptime={flags.isOptime}
            quantityVisible={quantityVisible}
          />
        </ProductLineGrid.Messages>
      </ProductLineGrid>

      <OptionalBox>
        {surchargeValue && <IncludedSurchargeAsterisk value={surchargeValue} percent={lineItem.netPrice?.surcharge} />}
        {tariffSurcharge && <TariffSurchargeAsterisk />}
      </OptionalBox>

      {kitAccordionSlot}

      <PartialDeliveriesSection partialDeliveries={lineItem.partialDeliveries} />

      <Stack direction={"row"} justifyContent={"space-between"}>
        <ToggleCommentButton open={lineItem.showComments} onClick={toggleComment} />
        <DeleteButton
          onClick={() => {
            actions.removeEntry();
            if (!inStock) {
              onDelete?.();
            }
          }}
          disabled={lineItem.isBundled && lineItem.original}
        >
          {t("cart.deleteLine")}
        </DeleteButton>
      </Stack>

      {lineItem.showComments && (
        <Box sx={{ width: { mobile: "100%", "desktop-m": "50%" } }}>
          <CommentInput
            value={lineItem.comment || ""}
            onChange={actions.updateComment}
            ref={commentRef}
            label={t("wishlistPage.comment.label")}
          />
        </Box>
      )}
    </Card>
  );
};
